import { useState } from 'react'
import { useTheme } from '../../ThemeContext'
import { getCollegeForDepartment, getColleges2026 } from '../../utils/deptUtils'
import { buildProfilePatch } from '../../utils/profileManagement'

export const CURRENT_YEAR = new Date().getFullYear()
export const YEARS = Array.from({ length: 8 }, (_, i) => String(CURRENT_YEAR - i))

export default function ProfileSetupModal({ initialProfile, onSave, onDismiss }) {
  const { isDark } = useTheme()
  const isEdit = !!initialProfile

  const [nickname,     setNickname]     = useState(initialProfile?.nickname     ?? '')
  const [college,      setCollege]      = useState(initialProfile?.college      ?? '')
  const [department,   setDepartment]   = useState(initialProfile?.department   ?? '')
  const [grade,        setGrade]        = useState(initialProfile?.grade        ?? '')
  const [entranceYear, setEntranceYear] = useState(initialProfile?.entranceYear ?? '')
  const [departmentCleared, setDepartmentCleared] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const T = {
    bg:        isDark ? '#1c1c1e' : '#ffffff',
    text:      isDark ? '#f5f5f7' : '#1a1a1a',
    sub:       isDark ? '#aeaeb2' : '#64748b',
    border:    isDark ? '#2c2c2e' : '#eaecf2',
    input:     isDark ? '#2c2c2e' : '#f5f5f7',
    inputText: isDark ? '#f5f5f7' : '#1a1a1a',
    placeholder: isDark ? '#636366' : '#aeaeb2',
  }

  const MAX_NICK = 10
  const nickTrimmed = nickname.trim()
  const nickLen = nickTrimmed.length
  const nickOverLimit = nickLen > MAX_NICK  // maxLength 속성으로 막히지만 방어적 체크

  async function handleSave() {
    if (!nickTrimmed || nickOverLimit || saving) return
    setSaving(true)
    setSaveError('')
    try {
      const patch = buildProfilePatch({ nickname: nickTrimmed, college, department, grade, entranceYear }, initialProfile)
      if (departmentCleared && department === '' && (initialProfile?.department ?? '') !== '') patch.department = ''
      if (!patch.nickname) patch.nickname = nickTrimmed
      await onSave(patch)
    } catch {
      setSaveError('저장하지 못했어요. 다시 시도해 주세요.')
    } finally {
      setSaving(false)
    }
  }

  function selectCollege(value) {
    setCollege(value)
    if (department && getCollegeForDepartment(department) !== value) setDepartment('')
  }

  function selectDepartment(value) {
    setDepartment(value)
    setDepartmentCleared(value === '')
    if (value) setCollege(current => getCollegeForDepartment(value) ?? current)
  }

  const canSave = nickLen >= 1 && !nickOverLimit && !saving

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 12, fontSize: 16,
    background: T.input, border: `1px solid ${T.border}`, color: T.inputText,
    outline: 'none', boxSizing: 'border-box',
    WebkitAppearance: 'none',
  }
  const labelStyle = {
    fontSize: 12, fontWeight: 700, color: T.sub,
    display: 'block', marginBottom: 6,
  }

  return (
    <div
      data-no-swipe-back=""
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.52)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
      onClick={onDismiss}
    >
      <div
        style={{
          background: T.bg,
          borderRadius: '20px 20px 0 0',
          padding: '8px 24px 40px',
          width: '100%', maxWidth: 480,
          boxShadow: '0 -4px 40px rgba(0,0,0,0.18)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 드래그 핸들 */}
        <div style={{ width: 40, height: 4, background: T.border, borderRadius: 2, margin: '12px auto 20px' }} />

        {/* 헤더 */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 16, margin: '0 auto 14px',
            background: isDark ? '#1a3a5c' : '#dbeafe',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26,
          }}>✏️</div>
          <p style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 8 }}>
            내 정보 수정
          </p>
          <p style={{ fontSize: 13, color: T.sub, lineHeight: 1.7 }}>
            SU-Guide가 시간표와 학사 정보를<br />더 정확히 안내할 수 있어요.
          </p>
        </div>

        {/* 섹션: 기본 정보 */}
        <p style={{ fontSize: 11, fontWeight: 800, color: T.sub, marginBottom: 10, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          기본 정보
        </p>

        {/* 닉네임 * 필수 */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>
              닉네임 <span style={{ color: '#0066cc' }}>*</span>
            </label>
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: nickLen > MAX_NICK ? '#dc2626' : T.sub,
            }}>
              {nickLen}/{MAX_NICK}
            </span>
          </div>
          <input
            type="text"
            maxLength={MAX_NICK}
            placeholder="최대 10자 (예: 예성, 학생01)"
            value={nickname}
            onChange={e => setNickname(e.target.value)}
            style={{
              ...inputStyle,
              border: `1px solid ${nickOverLimit ? '#dc2626' : T.border}`,
            }}
          />
          {nickOverLimit && (
            <p style={{ fontSize: 11, color: '#dc2626', marginTop: 5, fontWeight: 600 }}>
              닉네임은 10자 이하로 입력해 주세요
            </p>
          )}
        </div>

        {/* 섹션 구분선 + 학교 정보 */}
        <div style={{ height: 1, background: T.border, marginBottom: 16 }} />
        <p style={{ fontSize: 11, fontWeight: 800, color: T.sub, marginBottom: 10, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          학교 정보
        </p>

        {/* 대학 */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>대학 (선택)</label>
          <select
            value={college}
            onChange={e => selectCollege(e.target.value)}
            style={{ ...inputStyle, color: college ? T.inputText : T.placeholder, appearance: 'none', WebkitAppearance: 'none' }}
          >
            <option value="">선택</option>
            {getColleges2026().map(item => <option key={item.collegeName} value={item.collegeName}>{item.collegeName}</option>)}
          </select>
        </div>

        {/* 소속 */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>
            소속 (선택) <span style={{ fontWeight: 400, opacity: 0.7 }}>· 학부 · 학과 · 전공</span>
          </label>
          <select
            value={department}
            onChange={e => selectDepartment(e.target.value)}
            style={{ ...inputStyle, color: department ? T.inputText : T.placeholder, appearance: 'none', WebkitAppearance: 'none' }}
          >
            <option value="">아직 정하지 않았어요</option>
            {getColleges2026().filter(item => !college || item.collegeName === college).map(item => (
              <optgroup key={item.collegeName} label={item.collegeName}>
                {item.departments.map(dept => <option key={dept.departmentName} value={dept.departmentName}>{dept.departmentName}</option>)}
              </optgroup>
            ))}
          </select>
        </div>

        {/* 학년 + 입학년도 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 28 }}>
          <div>
            <label style={labelStyle}>학년 (선택)</label>
            <select
              value={grade}
              onChange={e => setGrade(e.target.value)}
              style={{ ...inputStyle, color: grade ? T.inputText : T.placeholder, appearance: 'none', WebkitAppearance: 'none' }}
            >
              <option value="">선택</option>
              {['1', '2', '3', '4'].map(g => (
                <option key={g} value={g}>{g}학년</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>입학년도 (선택)</label>
            <select
              value={entranceYear}
              onChange={e => setEntranceYear(e.target.value)}
              style={{ ...inputStyle, color: entranceYear ? T.inputText : T.placeholder, appearance: 'none', WebkitAppearance: 'none' }}
            >
              <option value="">선택</option>
              {YEARS.map(y => <option key={y} value={y}>{y}년</option>)}
            </select>
          </div>
        </div>

        {saveError && (
          <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 10, fontWeight: 600 }}>{saveError}</p>
        )}

        {/* 저장 버튼 */}
        <button
          onClick={handleSave}
          disabled={!canSave}
          aria-busy={saving}
          style={{
            width: '100%', padding: '15px', borderRadius: 14, marginBottom: 10,
            background: canSave ? '#0066cc' : (isDark ? '#2c2c2e' : '#e5e5ea'),
            border: 'none',
            color: canSave ? '#ffffff' : T.sub,
            fontSize: 15, fontWeight: 700,
            cursor: canSave ? 'pointer' : 'default',
            outline: 'none', WebkitTapHighlightColor: 'transparent',
            transition: 'all 0.15s',
          }}
        >
          {saving ? '저장 중...' : '저장하기'}
        </button>

        {/* 닫기 버튼 */}
        <button
          onClick={onDismiss}
          style={{
            width: '100%', padding: '14px', borderRadius: 14,
            background: 'none', border: `1px solid ${T.border}`,
            color: T.sub, fontSize: 14, fontWeight: 600,
            cursor: 'pointer', outline: 'none', WebkitTapHighlightColor: 'transparent',
          }}
        >
          {isEdit ? '닫기' : '나중에 설정하기'}
        </button>
      </div>
    </div>
  )
}
